/**
 * Prospecting — plan §17.5.
 *
 * The gap this closes: §4.2 lists the people-data vendor as "(Signals/Prospecting)"
 * and the plan then used it only for signals. An account with no contacts could
 * stall forever.
 *
 * Everything here is pure and VENDOR-NEUTRAL: a persona produces one canonical
 * query (ActOS's own seniorities, size bands and the shared LinkedIn industry
 * list), and the selected vendor's adapter translates it to that API's
 * spellings through ./vendors.ts. The three decisions worth testing without a
 * network are: what query a persona produces, how well a returned person
 * matches it, and what an email-validation verdict means for enrollability —
 * the last of which decides whether a real person gets emailed.
 */

import type { VerificationResult } from '../verification/index';
import { canonicalIndustry, suggestIndustries } from './industries';
import { canonicalSizeBand, VENDOR_FILTER_LIMITS } from './vendors';

export * from './industries';
export * from './vendors';

/**
 * ActOS's seniority vocabulary — §17.5.
 *
 * Historically Amplemarket's list, adopted as the canonical one because every
 * authored persona already holds these spellings and Amplemarket sends them
 * verbatim. ContactOut goes through `toContactOutSeniority`, which has no
 * answer for `Other` and `Non-Manager`. A value outside this list is dropped
 * from the query rather than sent (both vendors 400 on an unknown level).
 */
export const SENIORITIES = [
  'Owner',
  'Founder',
  'C-Suite',
  'Partner',
  'VP',
  'Head',
  'Director',
  'Manager',
  'Senior',
  'Entry',
  'Intern',
  'Other',
  'Non-Manager',
  'Founder/Owner',
] as const;

export type Seniority = (typeof SENIORITIES)[number];

/** @deprecated Use `SENIORITIES` — identical values; the list is ActOS's, not a vendor's. */
export const AMPLEMARKET_SENIORITIES = SENIORITIES;

/**
 * ActOS's company-size bands — the same eight `employeeBandFor` writes onto
 * `accounts.employee_band`, so a persona's band and an enriched account's band
 * compare directly. Adapters translate: `toAmplemarketSizeLabel` ('51-200 employees'),
 * `toContactOutSizeBand` ('51_200').
 */
export const SIZE_BANDS = [
  '1-10',
  '11-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  '10001+',
] as const;

export type SizeBand = (typeof SIZE_BANDS)[number];

/**
 * A headcount to its band — the ONE mapping, so a company of exactly ten
 * lands in the same band whichever connector wrote it. HubSpot's mapper had
 * its own boundaries (`< 10` where the vendor path said `<= 10`) and a top
 * band of `5000+` that no audience could ask for, so 5000-plus companies
 * synced from the CRM never matched a size filter.
 */
export function bandForCount(count: number | null | undefined): SizeBand | null {
  if (count === null || count === undefined || !Number.isFinite(count) || count <= 0) return null;
  if (count <= 10) return '1-10';
  if (count <= 50) return '11-50';
  if (count <= 200) return '51-200';
  if (count <= 500) return '201-500';
  if (count <= 1000) return '501-1000';
  if (count <= 5000) return '1001-5000';
  if (count <= 10000) return '5001-10000';
  return '10001+';
}

/** @deprecated Amplemarket's wire labels. Use `SIZE_BANDS` and `toAmplemarketSizeLabel`. */
export const AMPLEMARKET_SIZES = [
  '1-10 employees',
  '11-50 employees',
  '51-200 employees',
  '201-500 employees',
  '501-1000 employees',
  '1001-5000 employees',
  '5001-10000 employees',
  '10001+ employees',
] as const;

/** Case-insensitive resolution to the canonical spelling, or `null`. */
export function canonicalSeniority(value: string | null | undefined): Seniority | null {
  const wanted = value?.trim().toLowerCase();
  if (!wanted) return null;
  return SENIORITIES.find((s) => s.toLowerCase() === wanted) ?? null;
}

export interface PersonaQueryInput {
  id: number;
  name: string;
  titles: string[] | null;
  seniority: string | null;
  industries: string[] | null;
  companySizeBand: string | null;
}

/**
 * What a query may carry, per vendor. Structurally a subset of the port's
 * `PeopleDataProvider.capabilities`, so a route passes `provider.capabilities`
 * straight in.
 */
export interface QueryLimits {
  /** The vendor's per-filter option ceiling — Amplemarket 10, ContactOut 50. */
  maxFilterOptions: number;
}

/**
 * Amplemarket's ceiling: the stricter of the two, so a caller that has not
 * asked the vendor produces a query either vendor accepts.
 */
export const DEFAULT_QUERY_LIMITS: QueryLimits = {
  maxFilterOptions: VENDOR_FILTER_LIMITS.amplemarket,
};

export interface ProspectQuery {
  personaId: number;
  personaName: string;
  titles: string[];
  /** Canonical `SENIORITIES` values — zero or one; the adapter translates. */
  seniorities: Seniority[];
  /** Canonical `INDUSTRIES` spellings, at most `limits.maxFilterOptions`. */
  industries: string[];
  /** Canonical `SIZE_BANDS` values — zero or one; the adapter translates. */
  companySizes: SizeBand[];
  /**
   * Industries the persona names that the vocabulary does not contain, with
   * the nearest supported values. NOT sent — one unsupported value 400s an
   * Amplemarket request and silently finds nobody on ContactOut — and never
   * silently discarded either: an industry filter that vanishes turns a
   * precise ICP search into a market-wide one, and the results would look
   * like the ICP is broad rather than like the filter failed.
   */
  unsupportedIndustries: { value: string; suggestions: string[] }[];
  /**
   * Supported industries beyond the vendor's per-filter ceiling
   * (`limits.maxFilterOptions`). Sending them 400s the request, so they are
   * trimmed — and named, because a search quietly narrower than the persona
   * describes finds fewer ICP companies than it should and nothing would say why.
   */
  industriesOverflow: string[];
}

/**
 * Build one search query per persona — §17.5's "the query IS the persona".
 *
 * One authored object serves both the ranker's `icp_persona_match` (§8.3) and
 * the prospecting query, which is what personas are *for*. A persona with no
 * titles produces no query rather than an unfiltered one: searching a company
 * for "anybody" and revealing the first three is not prospecting, it is a
 * random charge — and on ContactOut the search itself is the charge.
 *
 * `limits` is the selected vendor's ceiling; the default is Amplemarket's,
 * which is the stricter, so an un-parameterised call is safe on either vendor.
 */
export function queryForPersona(
  persona: PersonaQueryInput,
  limits: QueryLimits = DEFAULT_QUERY_LIMITS,
): ProspectQuery | null {
  const titles = (persona.titles ?? []).map((t) => t.trim()).filter(Boolean);
  if (titles.length === 0) return null;

  const ceiling = Math.max(1, Math.floor(limits.maxFilterOptions));

  // An unrecognised seniority or band is dropped rather than sent: both
  // vendors reject the whole request for an invalid enum value, which would
  // turn a typo in the Library into "prospecting is broken".
  const seniority = canonicalSeniority(persona.seniority);
  const band = canonicalSizeBand(persona.companySizeBand);

  /**
   * Industries get the same treatment seniorities always had, for the same
   * reason — and this is where company discovery was breaking. Both vendors'
   * industry filters are closed vocabularies; Amplemarket rejects the ENTIRE
   * request on one bad value ("'Enterprise Software' is not a supported
   * industry"), ContactOut returns nobody. Canonicalised here, because the
   * APIs compare exactly.
   */
  const industries: string[] = [];
  const unsupportedIndustries: { value: string; suggestions: string[] }[] = [];
  for (const raw of persona.industries ?? []) {
    const value = raw.trim();
    if (!value) continue;
    const canonical = canonicalIndustry(value);
    if (canonical) industries.push(canonical);
    else unsupportedIndustries.push({ value, suggestions: suggestIndustries(value) });
  }

  // The vendor refuses more than `ceiling` options on this filter, so the
  // extras are trimmed here rather than 400ing the whole search.
  const industriesOverflow = industries.slice(ceiling);

  return {
    personaId: persona.id,
    personaName: persona.name,
    titles,
    seniorities: seniority ? [seniority] : [],
    industries: industries.slice(0, ceiling),
    companySizes: band ? [band] : [],
    unsupportedIndustries,
    industriesOverflow,
  };
}

// ---- Persona fit --------------------------------------------------------

export interface PersonaFit {
  score: number;
  reason: string;
}

const NOISE = /\b(of|the|and|for|at|in|to|a|an|&)\b/g;

function normaliseTitle(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(NOISE, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      /**
       * Rejoin initialisms that the punctuation strip pulled apart: "V.P."
       * became "v p", and would no longer match "vp". Real titles are written
       * both ways — "V.P. Operations" and "VP Operations" are one job — and
       * applying this to both sides keeps the comparison symmetric.
       */
      .replace(/\b(?:[a-z] )+[a-z]\b/g, (run) => run.replace(/ /g, ''))
  );
}

/**
 * How well a returned person matches the persona that asked for them.
 *
 * Shown in the review preview so a human can tell a real match from a loose
 * one before spending a credit. Deterministic on purpose — a model deciding
 * "is this person a VP Ops" would be both slower and unaccountable, and this
 * is a number someone will argue with.
 */
export function personaFit(personTitle: string | null, personaTitles: string[]): PersonaFit {
  if (!personTitle?.trim()) {
    return { score: 0, reason: 'No title returned, so nothing to match on.' };
  }

  const actual = normaliseTitle(personTitle);
  const actualWords = new Set(actual.split(' '));

  let best: PersonaFit = {
    score: 0.2,
    reason: 'Matched the search filters but not the persona’s titles.',
  };

  for (const raw of personaTitles) {
    const wanted = normaliseTitle(raw);
    if (!wanted) continue;

    if (actual === wanted) {
      return { score: 1, reason: `Exact match for “${raw}”.` };
    }

    /**
     * Contains, at WORD boundaries — the padding is load-bearing.
     *
     * A raw `actual.includes(wanted)` matched on letters, and "director"
     * contains the letters of "CTO" (dire-**cto**-r). Every Director in the
     * CRM therefore scored 0.8 against a CTO persona: "Marketing Director,
     * Europe" and "Director, Customer Marketing" were both reported as
     * technical leadership, here and in the §17.5 preview a human reads
     * before spending a credit.
     */
    if (` ${actual} `.includes(` ${wanted} `)) {
      best = better(best, { score: 0.8, reason: `Title contains “${raw}”.` });
      continue;
    }

    const wantedWords = wanted.split(' ');
    const overlap = wantedWords.filter((w) => actualWords.has(w)).length;

    if (overlap === wantedWords.length) {
      best = better(best, { score: 0.6, reason: `Every word of “${raw}” appears in the title.` });
    } else if (overlap > 0) {
      best = better(best, {
        score: 0.3 + 0.2 * (overlap / wantedWords.length),
        reason: `Partial overlap with “${raw}”.`,
      });
    }
  }

  return best;
}

function better(a: PersonaFit, b: PersonaFit): PersonaFit {
  return b.score > a.score ? b : a;
}

// ---- Email verification -------------------------------------------------

/**
 * @deprecated Use `VerificationResult` from `../verification`. The vocabulary
 * is ActOS's; Amplemarket uses it natively and ContactOut's is mapped by
 * `contactOutVerification`.
 */
export type AmplemarketValidationResult = VerificationResult;

/** ActOS's `verification_status` enum. */
export type VerificationStatus = 'unverified' | 'verified' | 'failed' | 'catch_all';

export interface VerificationVerdict {
  status: VerificationStatus;
  /** True only for `verified` — §17.5's readiness gate. */
  enrollable: boolean;
  /** Names the contact in the research task raised when it is not enrollable. */
  reason: string;
}

/**
 * Map a checker's verdict onto ActOS's, and decide enrollability — §17.5.
 *
 * **Only `deliverable` passes.** Everything else produces a research task
 * naming the contact rather than a silent block, which is the mockup's
 * "No verified contact — confirm the ops lead" state.
 *
 * `risky` is treated as not-enrollable even though some risky addresses work.
 * The asymmetry is deliberate: a wrongly-held contact costs a rep two minutes,
 * and a wrongly-sent one costs the sending domain's reputation, which is the
 * asset the whole system is built to protect (§5.3).
 */
export function verdictFor(result: VerificationResult, catchAll: boolean): VerificationVerdict {
  /**
   * Catch-all is checked FIRST, and it beats `deliverable`.
   *
   * A catch-all domain accepts mail for every address, existing or not — so
   * "deliverable" on one is not evidence, it is the domain's configuration
   * answering instead of the mailbox. Checking `deliverable` first would mark
   * every invented address at such a domain verified, which is how a system
   * cold-emails `dana.levi@` at a company where no Dana Levi works.
   */
  if (catchAll) {
    return {
      status: 'catch_all',
      enrollable: false,
      reason:
        'The domain accepts all mail, so a valid-looking address proves nothing about the mailbox. Confirm the person before enrolling them.',
    };
  }

  if (result === 'deliverable') {
    return {
      status: 'verified',
      enrollable: true,
      reason: 'The provider confirmed the address exists.',
    };
  }

  if (result === 'risky') {
    return {
      status: 'failed',
      enrollable: false,
      reason:
        'The address may bounce — a full, disposable or otherwise risky mailbox. Confirm it before enrolling them.',
    };
  }

  if (result === 'undeliverable') {
    return {
      status: 'failed',
      enrollable: false,
      reason: 'The address does not exist. Find the right one before enrolling them.',
    };
  }

  return {
    status: 'unverified',
    enrollable: false,
    reason:
      'The provider did not answer, so the address is unproven. Confirm it before enrolling them.',
  };
}

// ---- Caps ---------------------------------------------------------------

export interface RevealBudget {
  /** `prospecting.limit_per_persona` — §17.5's spend control. */
  limitPerPersona: number;
  /** Guards gate 6: eight contacts at one company is the pattern it suppresses. */
  maxNewPerAccount: number;
  existingContactsAtAccount: number;
}

export interface RevealDecision {
  allowed: string[];
  refused: { candidateId: string; reason: string }[];
}

/**
 * Decide which of the selected candidates may be revealed — §17.5.
 *
 * Two independent caps, and both are reported per refusal rather than as one
 * number. "3 of 7 were skipped" invites a second run to get the rest, which is
 * exactly what the account cap exists to stop.
 */
export function applyRevealCaps(
  selected: readonly { id: string; personaId: number | null }[],
  budget: RevealBudget,
): RevealDecision {
  const allowed: string[] = [];
  const refused: RevealDecision['refused'] = [];

  const perPersona = new Map<number | null, number>();
  let accountRoom = Math.max(0, budget.maxNewPerAccount - budget.existingContactsAtAccount);

  for (const candidate of selected) {
    if (accountRoom <= 0) {
      refused.push({
        candidateId: candidate.id,
        reason: `This account is at its cap of ${budget.maxNewPerAccount} contacts. Contacting more people at one company is the pattern gate 6 exists to suppress.`,
      });
      continue;
    }

    const used = perPersona.get(candidate.personaId) ?? 0;
    if (used >= budget.limitPerPersona) {
      refused.push({
        candidateId: candidate.id,
        reason: `Already revealing ${budget.limitPerPersona} for this persona, which is the per-run reveal limit and the spend control.`,
      });
      continue;
    }

    perPersona.set(candidate.personaId, used + 1);
    accountRoom -= 1;
    allowed.push(candidate.id);
  }

  return { allowed, refused };
}
